import { Fragment, useState } from 'react'
import { useLang } from '../i18n/LangProvider'
import { t, Lang, umbralMinutaje, UMBRAL_COLOR, PillBaja } from './i18n'
import { DashboardMiembro, JornadaResumen } from './model'
import { EmptyState } from '../styles/EmptyState'
import { PanelEstadisticasJugador } from './PanelEstadisticasJugador'
import { PILL_COLOR } from './pillColors'
import { formatDate, localeFor } from '../utils/format'
import styles from './DashboardMinutaje.module.css'

type Resultado = 'V' | 'E' | 'D'

function resultadoJornada(golesFavor: number, golesContra: number): Resultado {
  if (golesFavor > golesContra) return 'V'
  if (golesFavor < golesContra) return 'D'
  return 'E'
}

const RESULTADO_COLOR: Record<Resultado, string> = {
  V: 'var(--color-success)',
  E: 'var(--color-neutral)',
  D: 'var(--color-danger)',
}

function PctCell({ pct }: { pct: number | '--' }) {
  const nivel = umbralMinutaje(pct)
  return <span style={{ color: UMBRAL_COLOR[nivel] }}>{pct === '--' ? '--' : `${pct}%`}</span>
}

// Pill de CONV/JUG/TIT/baja con el mismo color que en la fila de la jornada.
function Pill({ label, color }: { label: string; color: string }) {
  return (
    <span className={styles.pill} style={{ backgroundColor: color }}>
      {label}
    </span>
  )
}

const BAJA_KEY: Record<PillBaja, 'les' | 'san' | 'enf' | 'vac' | 'tr' | 'nj'> = {
  LES: 'les',
  SAN: 'san',
  ENF: 'enf',
  VAC: 'vac',
  TR: 'tr',
  NJ: 'nj',
}

type Participacion = JornadaResumen['participaciones'][number]

// Titulares, luego jugados, luego convocados, luego el resto (bajas / no convocados).
function ordenParticipacion(p: Participacion): number {
  if (p.titular) return 0
  if (p.jugado) return 1
  if (p.convocado) return 2
  return p.baja ? 3 : 4
}

// Look & feel (2026-10-22): "el detalle por jornada se ve bastante feo… seleccionar tabla o fichas no
// cambia nada… la lista de jugadores en alerta se podría tabular". Antes el detalle listaba
// `✓ J T` sin el nombre del jugador y los botones Tabla/Fichas solo cambiaban cuál estaba marcado.
export function DashboardMinutaje({
  equipoId,
  dashboard,
  historial,
  duracionLabel,
  lang: langProp,
  conTarjetas = false,
}: {
  equipoId: string
  dashboard: DashboardMiembro[]
  historial: JornadaResumen[]
  duracionLabel: string
  lang?: Lang
  conTarjetas?: boolean
}) {
  const ctxLang = useLang()
  const lang = langProp ?? ctxLang
  const locale = localeFor(lang)
  // Feedback de navegación del cliente, 2026-10-02: "en dashboard hay una lista pero no hay click,
  // no hace nada" — el nombre del jugador en esta tabla era texto plano; ahora abre el mismo panel
  // de estadísticas ya disponible en la vista de Jornada (📊).
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [detalleIndex, setDetalleIndex] = useState(0)
  const [vistaDetalle, setVistaDetalle] = useState<'tabla' | 'fichas'>('tabla')

  const nombrePorMiembro = new Map(dashboard.map((m) => [m.miembro_equipo_id, m.persona.nombre]))
  const alertas = dashboard
    .filter((m) => m.alerta !== null)
    .sort((a, b) => {
      if (a.alerta !== b.alerta) return a.alerta === 'intervenir' ? -1 : 1
      const pa = typeof a.porcentaje_disp === 'number' ? a.porcentaje_disp : 0
      const pb = typeof b.porcentaje_disp === 'number' ? b.porcentaje_disp : 0
      return pa - pb
    })

  const balance = historial.reduce(
    (acc, j) => {
      acc[resultadoJornada(j.goles_favor, j.goles_contra)] += 1
      acc.gf += j.goles_favor
      acc.gc += j.goles_contra
      return acc
    },
    { V: 0, E: 0, D: 0, gf: 0, gc: 0 },
  )

  // `historial` viene de la más reciente a la más antigua: índice 0 = última jornada.
  const jornadaDetalle = historial[detalleIndex]
  const participaciones = jornadaDetalle
    ? [...jornadaDetalle.participaciones]
        .filter((p) => nombrePorMiembro.has(p.miembro_equipo_id))
        .sort((a, b) => ordenParticipacion(a) - ordenParticipacion(b))
    : []

  return (
    <div className={styles.dashboard}>
      <h3>{t('kpiEquipo', lang)}</h3>
      <dl className="stat-card-grid">
        <div className="stat-card">
          <dt>{t('jornadas', lang)}</dt>
          <dd>{historial.length}</dd>
        </div>
        <div className="stat-card">
          <dt>{t('balance', lang)}</dt>
          <dd aria-label="balance">
            {balance.V}-{balance.E}-{balance.D}
          </dd>
        </div>
        <div className="stat-card">
          <dt>{t('golesFavorContra', lang)}</dt>
          <dd>
            {balance.gf} / {balance.gc}
          </dd>
        </div>
      </dl>

      <h3>
        {t('controlParticipacion', lang)} — <span aria-label="duracion-partido">{duracionLabel}</span>
      </h3>
      <p className={styles.leyenda}>{t('leyendaExplicacion', lang)}</p>

      <section>
        <h4>{t('panelAlertas', lang)}</h4>
        {alertas.length === 0 ? (
          <p className={styles.vacio}>—</p>
        ) : (
          <div className="table-scroll">
            <table className={`${styles.tabla} sticky-first-col`} aria-label={t('panelAlertas', lang)}>
              <thead>
                <tr>
                  <th>{t('jugador', lang)}</th>
                  <th>{t('porcDisp', lang)}</th>
                  <th>{t('estado', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {alertas.map((m) => (
                  <tr key={m.miembro_equipo_id}>
                    <td>{m.persona.nombre}</td>
                    <td>
                      <PctCell pct={m.porcentaje_disp} />
                    </td>
                    <td>
                      <span className={m.alerta === 'intervenir' ? 'pill pill-danger' : 'pill pill-warning'}>
                        {m.alerta === 'intervenir' ? t('alertaIntervenir', lang) : t('alertaVigilar', lang)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Look & feel (club, 2026-10-23): cabecera fija al hacer scroll vertical (`sticky-head`, App.css). */}
      <div className="table-scroll sticky-head">
        <table className={`${styles.tabla} sticky-first-col`}>
          <thead>
            <tr>
              <th>{t('jugador', lang)}</th>
              <th>{t('porcTotal', lang)}</th>
              <th>{t('porcConv', lang)}</th>
              <th>{t('porcDisp', lang)}</th>
            </tr>
          </thead>
          <tbody>
            {dashboard.map((m) => (
              <Fragment key={m.miembro_equipo_id}>
                <tr>
                  <td>
                    <button
                      className={styles.nombreBoton}
                      onClick={() => setExpandedId(expandedId === m.miembro_equipo_id ? null : m.miembro_equipo_id)}
                    >
                      {m.persona.nombre}
                    </button>
                  </td>
                  <td>
                    <PctCell pct={m.porcentaje_total} />
                  </td>
                  <td>
                    <PctCell pct={m.porcentaje_conv} />
                  </td>
                  <td>
                    <PctCell pct={m.porcentaje_disp} />
                  </td>
                </tr>
                {expandedId === m.miembro_equipo_id && (
                  <tr>
                    <td colSpan={4}>
                      <PanelEstadisticasJugador
                        equipoId={equipoId}
                        miembroEquipoId={m.miembro_equipo_id}
                        lang={lang}
                        conTarjetas={conTarjetas}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <section className={styles.detalle}>
        <h4>{t('detallePorJornada', lang)}</h4>
        {historial.length === 0 || !jornadaDetalle ? (
          <EmptyState text={t('sinJornadas', lang)} icon="📅" />
        ) : (
          <>
            <div className={styles.detalleCabecera}>
              <button
                onClick={() => setDetalleIndex((i) => Math.min(i + 1, historial.length - 1))}
                disabled={detalleIndex >= historial.length - 1}
                aria-label="jornada-anterior"
                title={t('jornadaAnterior', lang)}
              >
                ‹
              </button>
              <div className={styles.jornadaInfo}>
                <strong>
                  {t('jornadaTab', lang)} {jornadaDetalle.numero}
                  {jornadaDetalle.rival ? ` · ${jornadaDetalle.rival}` : ''}
                </strong>
                <span className={styles.jornadaMeta}>
                  {jornadaDetalle.fecha && <span>{formatDate(jornadaDetalle.fecha, locale)}</span>}
                  {(() => {
                    const r = resultadoJornada(jornadaDetalle.goles_favor, jornadaDetalle.goles_contra)
                    return (
                      <span className={styles.resultado} style={{ backgroundColor: RESULTADO_COLOR[r] }}>
                        {r} {jornadaDetalle.goles_favor}-{jornadaDetalle.goles_contra}
                      </span>
                    )
                  })()}
                </span>
              </div>
              <button
                onClick={() => setDetalleIndex((i) => Math.max(i - 1, 0))}
                disabled={detalleIndex === 0}
                aria-label="jornada-siguiente"
                title={t('jornadaSiguiente', lang)}
              >
                ›
              </button>
            </div>

            <div className="tabs" role="tablist">
              <button role="tab" aria-selected={vistaDetalle === 'tabla'} onClick={() => setVistaDetalle('tabla')} disabled={vistaDetalle === 'tabla'}>
                {t('tablaToggle', lang)}
              </button>
              <button role="tab" aria-selected={vistaDetalle === 'fichas'} onClick={() => setVistaDetalle('fichas')} disabled={vistaDetalle === 'fichas'}>
                {t('fichasToggle', lang)}
              </button>
            </div>

            {vistaDetalle === 'tabla' ? (
              <div className="table-scroll sticky-head">
                <table className={`${styles.tabla} ${styles.tablaDetalle} sticky-first-col`} aria-label="detalle-tabla">
                  <thead>
                    <tr>
                      <th>{t('jugador', lang)}</th>
                      <th>{t('conv', lang)}</th>
                      <th>{t('jug', lang)}</th>
                      <th>{t('tit', lang)}</th>
                      <th>{t('min', lang)}</th>
                      <th>{t('goles', lang)}</th>
                      {conTarjetas && <th aria-label={t('amarillas', lang)}>🟨</th>}
                      {conTarjetas && <th aria-label={t('rojas', lang)}>🟥</th>}
                      <th>{t('baja', lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participaciones.map((p) => (
                      <tr key={p.miembro_equipo_id} className={p.convocado ? undefined : styles.filaNoConvocado}>
                        <td>{nombrePorMiembro.get(p.miembro_equipo_id)}</td>
                        <td>{p.convocado ? <span style={{ color: PILL_COLOR.conv }}>✓</span> : ''}</td>
                        <td>{p.jugado ? <span style={{ color: PILL_COLOR.jug }}>✓</span> : ''}</td>
                        <td>{p.titular ? <span style={{ color: PILL_COLOR.tit }}>✓</span> : ''}</td>
                        <td>{p.minutos || ''}</td>
                        <td>{p.goles || ''}</td>
                        {conTarjetas && <td>{p.amarillas || ''}</td>}
                        {conTarjetas && <td>{p.rojas || ''}</td>}
                        <td>
                          {p.baja && (
                            <Pill label={t(BAJA_KEY[p.baja as PillBaja] ?? 'nj', lang)} color={PILL_COLOR[p.baja as PillBaja] ?? 'var(--color-neutral)'} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <ul className={styles.fichas} aria-label="detalle-fichas">
                {participaciones.map((p) => (
                  <li key={p.miembro_equipo_id} className={`${styles.ficha} ${p.convocado ? '' : styles.filaNoConvocado}`}>
                    <strong className={styles.fichaNombre}>{nombrePorMiembro.get(p.miembro_equipo_id)}</strong>
                    <div className={styles.fichaPills}>
                      {p.convocado && <Pill label={t('conv', lang)} color={PILL_COLOR.conv} />}
                      {p.jugado && <Pill label={t('jug', lang)} color={PILL_COLOR.jug} />}
                      {p.titular && <Pill label={t('tit', lang)} color={PILL_COLOR.tit} />}
                      {p.baja && (
                        <Pill label={t(BAJA_KEY[p.baja as PillBaja] ?? 'nj', lang)} color={PILL_COLOR[p.baja as PillBaja] ?? 'var(--color-neutral)'} />
                      )}
                      {!p.convocado && !p.baja && <span className={styles.vacio}>{t('noConvocado', lang)}</span>}
                    </div>
                    {(p.minutos > 0 || p.goles > 0 || (conTarjetas && (p.amarillas > 0 || p.rojas > 0))) && (
                      <span className={styles.fichaDatos}>
                        {p.minutos > 0 && `${p.minutos} ${t('min', lang)}`}
                        {p.goles > 0 && ` · ${p.goles} ${t('gol', lang)}`}
                        {conTarjetas && p.amarillas > 0 && ` · ${'🟨'.repeat(p.amarillas)}`}
                        {conTarjetas && p.rojas > 0 && ` · 🟥`}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>
    </div>
  )
}
